// Which company the management screens are acting for — APP-1 (`TBS-47`).
//
// Pure, and imports nothing but the build-time default. The choosing lives here so it can be tested
// as a rule rather than through a dropdown.
//
// ─────────────────────────────────────────────────────────────────────────────────────────────────
// Until now the company was `REACT_APP_TBS_TENANT_ID`, fixed **at build time**. Working as a second
// company meant recompiling the application, which is not something a running system should ask of
// anyone — and the plan has said since §1 that you sign in and *then* choose.
//
// Everything needed was already in place: the Post-Login Action puts `tbs_tenants` on the token, the
// authorizer reads that claim, `GET /v1/me` returns the same list, and `rolesForTenant` already
// takes a company id. The only missing piece was something that chooses.
// ─────────────────────────────────────────────────────────────────────────────────────────────────

import { TBS_TENANT_ID } from "../config";

export interface TbsMembership {
  id: string;
  slug: string;
  roles: string[];
}

/** Survives a page reload. Scoped by name so it cannot collide with the storefront's own keys. */
export const TENANT_STORAGE_KEY = "tbs.tenantId";

/**
 * The memberships on the Auth0 user, defensively.
 *
 * The claim is written by an Auth0 Action and read here — two systems that can be changed
 * independently — so anything that is not a well-formed list of `{id, slug, roles}` is treated as
 * no memberships at all. Which is the correct answer: no membership means no access, everywhere.
 */
export const readMemberships = (user: unknown): TbsMembership[] => {
  const claim = (user as { tbs_tenants?: unknown } | null | undefined)?.tbs_tenants;
  if (!Array.isArray(claim)) return [];
  return claim
    .filter((m): m is TbsMembership => Boolean(m) && typeof (m as TbsMembership).id === "string")
    .map((m) => ({
      id: m.id,
      slug: typeof m.slug === "string" ? m.slug : m.id,
      roles: Array.isArray(m.roles) ? m.roles : [],
    }));
};

/**
 * Which company to act for, given what the token allows and what was chosen last.
 *
 * The order matters and each step has a reason:
 *
 * 1. **The stored choice, if the token still allows it.** Someone who picked a company expects to
 *    come back to it.
 * 2. **The build-time variable, if the token allows it.** Keeps existing deployments behaving
 *    exactly as they did, so this change is not a cutover.
 * 3. **The only membership**, when there is exactly one — matching what the authorizer does when no
 *    `X-Tenant-Id` is sent, and what `rolesForTenant` has always done.
 * 4. **Nothing.** With several memberships and no stored choice, the answer is genuinely "ask", and
 *    guessing the first would silently put someone in a company they did not pick.
 *
 * **A stored id the token no longer carries is dropped, not sent.** Access can be revoked between
 * one session and the next; sending it would produce a refusal on every request, which reads as the
 * application being broken rather than as permission having changed.
 */
export const resolveTenantId = (
  memberships: TbsMembership[],
  stored: string | null,
  configured: string = TBS_TENANT_ID,
): string => {
  const allowed = (id: string | null | undefined) =>
    Boolean(id) && memberships.some((m) => m.id === id);

  if (allowed(stored)) return stored as string;
  if (allowed(configured)) return configured;
  if (memberships.length === 1) return memberships[0].id;
  return "";
};

/** True when the person has a choice to make. One company is not a choice. */
export const shouldOfferChoice = (memberships: TbsMembership[]): boolean => memberships.length > 1;

// ── Persistence ──────────────────────────────────────────────────────────────────────────────────
//
// Wrapped in try/catch because `localStorage` throws rather than degrading in Safari private mode
// and when a browser blocks storage entirely. A company selector is not worth a blank screen; the
// cost of failing is that the choice does not survive a reload.

export const loadStoredTenantId = (): string | null => {
  try {
    return window.localStorage.getItem(TENANT_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const storeTenantId = (tenantId: string): void => {
  try {
    if (tenantId) window.localStorage.setItem(TENANT_STORAGE_KEY, tenantId);
    else window.localStorage.removeItem(TENANT_STORAGE_KEY);
  } catch {
    /* not worth failing over — see above */
  }
};

/** What the selector shows. The slug, because the legal name is not on the token. */
export const tenantLabel = (membership: TbsMembership | undefined): string =>
  membership ? membership.slug : "";
